(function (global) {
  'use strict';

  const ROOT_SELECTOR = '[data-kagushop-scene-coordinate]';
  const TRACK_SELECTOR = '[data-kagushop-scene-coordinate-track]';
  const CARD_SELECTOR = '[data-scene-card]';
  const CONTROLS_SELECTOR = '[data-kagushop-scene-carousel-controls]';
  const SLIDER_SELECTOR = '[data-scene-slider]';
  const RAIL_SELECTOR = '[data-scene-slider-rail]';
  const THUMB_SELECTOR = '[data-scene-slider-thumb]';
  const MIN_THUMB_WIDTH = 64;
  const READY_KEY = 'kgSceneCarouselReady';
  const DRAG_THRESHOLD = 6;
  const rootControllers = new WeakMap();
  const installedDocuments = new WeakMap();

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function nearestCardIndex(track, cards) {
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    cards.forEach(function (card, index) {
      const distance = Math.abs(card.offsetLeft - track.scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    return bestIndex;
  }

  function sliderMetrics(track, rail) {
    const railRect = rail.getBoundingClientRect();
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const share = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
    const thumbWidth = clamp(
      railRect.width * share,
      Math.min(MIN_THUMB_WIDTH, railRect.width),
      railRect.width
    );
    return {
      railLeft: railRect.left,
      railWidth: railRect.width,
      thumbWidth: thumbWidth,
      maxThumbTravel: Math.max(0, railRect.width - thumbWidth),
      maxScroll: maxScroll,
    };
  }

  function syncSlider(track, cards, controls, slider, rail, cachedMetrics) {
    const metrics = cachedMetrics || sliderMetrics(track, rail);
    const progress = metrics.maxScroll > 0 ? clamp(track.scrollLeft / metrics.maxScroll, 0, 1) : 0;
    const activeIndex = nearestCardIndex(track, cards);
    slider.style.setProperty('--kg-scene-slider-thumb-width', `${Math.round(metrics.thumbWidth)}px`);
    slider.style.setProperty('--kg-scene-slider-thumb-x', `${Math.round(progress * metrics.maxThumbTravel)}px`);
    slider.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
    slider.setAttribute('aria-valuetext', `${activeIndex + 1} / ${cards.length}`);
    controls.hidden = metrics.maxScroll <= 0;
    return metrics;
  }

  function initRoot(root, env) {
    env = env || global;
    if (!root || root.dataset[READY_KEY] === 'true') return false;
    const track = root.querySelector(TRACK_SELECTOR);
    const cards = Array.from(root.querySelectorAll(CARD_SELECTOR));
    const controls = root.querySelector(CONTROLS_SELECTOR);
    const slider = root.querySelector(SLIDER_SELECTOR);
    const rail = root.querySelector(RAIL_SELECTOR);
    const thumb = root.querySelector(THUMB_SELECTOR);
    if (!track || cards.length < 2 || !controls || !slider || !rail || !thumb) return false;

    const state = {
      active: false,
      dragged: false,
      suppressClick: false,
      startX: 0,
      startScrollLeft: 0,
      pointerId: null,
      sliderGesture: null,
      sliderSyncFrame: null,
      sliderSyncMetrics: null,
    };
    const reducedMotion = Boolean(
      env.matchMedia && env.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
    const canMouseDrag = function () {
      const mobile = !env.matchMedia || env.matchMedia('(max-width: 749px)').matches;
      return mobile && track.scrollWidth > track.clientWidth;
    };
    const isMobile = function () {
      return !env.matchMedia || env.matchMedia('(max-width: 749px)').matches;
    };
    const refreshSlider = function (metrics) {
      if (!isMobile() || track.scrollWidth <= track.clientWidth) {
        controls.hidden = true;
        return;
      }
      controls.hidden = false;
      syncSlider(track, cards, controls, slider, rail, metrics);
    };
    const scheduleSliderSync = function (metrics) {
      if (metrics) state.sliderSyncMetrics = metrics;
      if (!env.requestAnimationFrame) {
        const syncMetrics = state.sliderSyncMetrics;
        state.sliderSyncMetrics = null;
        refreshSlider(syncMetrics);
        return;
      }
      if (state.sliderSyncFrame !== null) return;
      state.sliderSyncFrame = env.requestAnimationFrame(function () {
        const syncMetrics = state.sliderSyncMetrics;
        state.sliderSyncFrame = null;
        state.sliderSyncMetrics = null;
        refreshSlider(syncMetrics);
      });
    };
    const scrollToCard = function (index) {
      const safeIndex = clamp(index, 0, cards.length - 1);
      track.scrollTo({
        left: cards[safeIndex].offsetLeft,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
      scheduleSliderSync();
    };
    const updateFromPointer = function (clientX, grabOffset, metrics) {
      if (metrics.maxScroll <= 0 || metrics.maxThumbTravel <= 0) return;
      const progress = clamp(
        (clientX - metrics.railLeft - grabOffset) / metrics.maxThumbTravel,
        0,
        1
      );
      track.scrollLeft = progress * metrics.maxScroll;
      scheduleSliderSync(metrics);
    };
    const releaseSliderCapture = function (gesture) {
      if (!gesture || !gesture.captured) return;
      slider.releasePointerCapture(gesture.pointerId);
      gesture.captured = false;
    };
    const clearSliderGesture = function (gesture) {
      releaseSliderCapture(gesture);
      if (state.sliderGesture === gesture) state.sliderGesture = null;
    };

    track.addEventListener('scroll', function () {
      scheduleSliderSync();
    }, { passive: true });

    slider.addEventListener('pointerdown', function (event) {
      if (!isMobile() || track.scrollWidth <= track.clientWidth) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.isPrimary === false || state.sliderGesture) return;
      const metrics = sliderMetrics(track, rail);
      const progress = metrics.maxScroll > 0
        ? clamp(track.scrollLeft / metrics.maxScroll, 0, 1)
        : 0;
      const thumbLeft = progress * metrics.maxThumbTravel;
      const pointerOnRail = event.clientX - metrics.railLeft;
      const startedOnThumb = (
        pointerOnRail >= thumbLeft && pointerOnRail <= thumbLeft + metrics.thumbWidth
      );
      state.sliderGesture = {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: Number.isFinite(event.clientY) ? event.clientY : 0,
        startScrollLeft: track.scrollLeft,
        grabOffset: startedOnThumb
          ? pointerOnRail - thumbLeft
          : metrics.thumbWidth / 2,
        startedOnThumb: startedOnThumb,
        accepted: false,
        verticalIntent: false,
        captured: false,
        metrics: metrics,
      };
      if (event.pointerType === 'mouse') {
        slider.setPointerCapture(event.pointerId);
        state.sliderGesture.captured = true;
      }
    });

    slider.addEventListener('pointermove', function (event) {
      const gesture = state.sliderGesture;
      if (!gesture || event.pointerId !== gesture.pointerId || gesture.verticalIntent) return;
      const deltaX = event.clientX - gesture.startX;
      const clientY = Number.isFinite(event.clientY) ? event.clientY : gesture.startY;
      const deltaY = clientY - gesture.startY;
      if (!gesture.accepted) {
        const touchLike = gesture.pointerType === 'touch' || gesture.pointerType === 'pen';
        if (touchLike && Math.abs(deltaY) >= DRAG_THRESHOLD && Math.abs(deltaY) >= Math.abs(deltaX)) {
          gesture.verticalIntent = true;
          return;
        }
        if (Math.abs(deltaX) < DRAG_THRESHOLD || (touchLike && Math.abs(deltaX) <= Math.abs(deltaY))) {
          return;
        }
        gesture.accepted = true;
        if (!gesture.captured) {
          slider.setPointerCapture(gesture.pointerId);
          gesture.captured = true;
        }
      }
      event.preventDefault();
      updateFromPointer(event.clientX, gesture.grabOffset, gesture.metrics);
    });

    slider.addEventListener('pointerup', function (event) {
      const gesture = state.sliderGesture;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      if (gesture.verticalIntent) {
        clearSliderGesture(gesture);
        return;
      }
      if (!gesture.accepted && !gesture.startedOnThumb) {
        event.preventDefault();
        updateFromPointer(event.clientX, gesture.metrics.thumbWidth / 2, gesture.metrics);
      } else if (gesture.accepted) {
        event.preventDefault();
        updateFromPointer(event.clientX, gesture.grabOffset, gesture.metrics);
      }
      const shouldSnap = !gesture.accepted && !gesture.startedOnThumb;
      clearSliderGesture(gesture);
      if (!shouldSnap) return;
      scrollToCard(nearestCardIndex(track, cards));
    });

    slider.addEventListener('pointercancel', function (event) {
      const gesture = state.sliderGesture;
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      track.scrollLeft = gesture.startScrollLeft;
      scheduleSliderSync(gesture.metrics);
      clearSliderGesture(gesture);
    });
    slider.addEventListener('keydown', function (event) {
      const current = nearestCardIndex(track, cards);
      let next = current;
      if (event.key === 'ArrowLeft') next = current - 1;
      else if (event.key === 'ArrowRight') next = current + 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = cards.length - 1;
      else return;
      event.preventDefault();
      scrollToCard(next);
    });

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !canMouseDrag()) return;
      state.active = true;
      state.dragged = false;
      state.startX = event.clientX;
      state.startScrollLeft = track.scrollLeft;
      state.pointerId = event.pointerId;
      track.setPointerCapture(event.pointerId);
    });

    track.addEventListener('pointermove', function (event) {
      if (!state.active || event.pointerType !== 'mouse' || event.pointerId !== state.pointerId) return;
      const delta = event.clientX - state.startX;
      if (!state.dragged && Math.abs(delta) < DRAG_THRESHOLD) return;
      state.dragged = true;
      track.classList.add('is-dragging');
      event.preventDefault();
      track.scrollLeft = state.startScrollLeft - delta;
    });

    function finishPointer(event) {
      if (!state.active || event.pointerId !== state.pointerId) return;
      if (state.dragged) state.suppressClick = true;
      state.active = false;
      state.pointerId = null;
      track.classList.remove('is-dragging');
      track.releasePointerCapture(event.pointerId);
    }

    track.addEventListener('pointerup', finishPointer);
    track.addEventListener('pointercancel', finishPointer);
    track.addEventListener('click', function (event) {
      if (!state.suppressClick) return;
      state.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    root.dataset[READY_KEY] = 'true';
    rootControllers.set(root, { refresh: refreshSlider });
    refreshSlider();
    return true;
  }

  function collectRoots(scope) {
    const roots = [];
    if (scope && scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope && scope.querySelectorAll) {
      roots.push.apply(roots, Array.from(scope.querySelectorAll(ROOT_SELECTOR)));
    }
    return roots;
  }

  function initAll(scope, env) {
    env = env || global;
    if (!scope) return 0;
    return collectRoots(scope).reduce(function (count, root) {
      return count + (initRoot(root, env) ? 1 : 0);
    }, 0);
  }

  function install(doc, env) {
    env = env || global;
    if (!doc || installedDocuments.has(doc)) return false;
    const roots = new Set();
    const registerScope = function (scope) {
      collectRoots(scope).forEach(function (root) {
        initRoot(root, env);
        if (rootControllers.has(root)) roots.add(root);
      });
    };
    const unregisterScope = function (scope) {
      collectRoots(scope).forEach(function (root) {
        roots.delete(root);
      });
    };
    const refreshConnectedRoots = function () {
      roots.forEach(function (root) {
        if (root.isConnected === false) {
          roots.delete(root);
          return;
        }
        const controller = rootControllers.get(root);
        if (controller) controller.refresh();
        else roots.delete(root);
      });
    };
    const start = function () {
      registerScope(doc);
    };
    installedDocuments.set(doc, { roots: roots, refresh: refreshConnectedRoots });
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
    doc.addEventListener('shopify:section:load', function (event) {
      registerScope(event.target);
    });
    doc.addEventListener('shopify:section:unload', function (event) {
      unregisterScope(event.target);
    });
    if (env.addEventListener) env.addEventListener('resize', refreshConnectedRoots, { passive: true });
    return true;
  }

  const api = {
    nearestCardIndex: nearestCardIndex,
    sliderMetrics: sliderMetrics,
    initRoot: initRoot,
    initAll: initAll,
    install: install,
  };
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else if (!global.KagushopSceneCoordinateCarousel) {
    global.KagushopSceneCoordinateCarousel = api;
    install(global.document, global);
  } else {
    global.KagushopSceneCoordinateCarousel.initAll(global.document, global);
  }
})(typeof window !== 'undefined' ? window : globalThis);
